import React, { useState, useEffect, useCallback, useRef, useMemo, useTransition } from 'react'
import { createPortal } from 'react-dom'
import {
    ChevronLeft,
    ChevronRight,
    Play,
    Pause,
    SkipForward,
    SkipBack,
    Clock,
    Zap,
    ChevronDown,
    ChevronUp,
    Pin,
    PinOff,
    TrendingUp,
    TrendingDown,
    RotateCcw,
    Search,
    Check,
    Sparkles,
    X,
    ShieldAlert,
} from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { useSettingsStore } from '../../stores/settingsStore'
import { usePlaybackStore } from '../../stores/playbackStore'
import { useMarketStore } from '../../services/store'
import { formatISTTime } from '../../lib/date-utils'
import { cn } from '../../lib/utils'
import {
    MARKET_HOURS,
    TRADING_PSYCHOLOGY_CONFIG,
    PLAYBACK_SPEEDS,
    PLAYBACK_SPEED_LABELS,
} from '../../config/constants'
import { useActiveHistory } from '../../hooks/useDataBus'
import { extractSpotPrice, getHistoryIndex } from '../../lib/snapshot-search'

export interface TimeRangeSlicerProps {
    minMinutes?: number
    maxMinutes?: number
    onChange: (startStr: string, endStr: string) => void
    viewMode: 'snapshot' | 'comparison'
    onViewModeChange: (mode: 'snapshot' | 'comparison') => void
    availableTimes?: string[] // HH:mm format from intraday history
    className?: string
    hideModeToggle?: boolean
    intervalMinutes?: number
    onIntervalChange?: (interval: number) => void
    /** When true, always initialise to full-day (maxMinutes) and ignore localStorage */
    forceFullDay?: boolean
    /** Index LTP data for the chip display */
    indexLtp?: number
    dayChange?: number
    dayChangeP?: number
    symbol?: string
}

const PRESETS = [
    { label: '5M', min: 5 },
    { label: '15M', min: 15 },
    { label: '30M', min: 30 },
    { label: 'Day', min: null },
]

const SPEEDS = PLAYBACK_SPEEDS

const formatMinutes = (m: number) => {
    const hh = Math.floor(m / 60)
    const mm = m % 60
    const ampm = hh >= 12 ? 'PM' : 'AM'
    let dispH = hh % 12
    if (dispH === 0) dispH = 12
    return `${dispH}:${mm.toString().padStart(2, '0')} ${ampm}`
}

const toTimeString = (m: number) => {
    const hh = Math.floor(m / 60)
    const mm = m % 60
    return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`
}

const timeStrToMin = (t: string) => {
    const [h, m] = t.split(':').map(Number)
    return (h || 0) * 60 + (m || 0)
}

const SLIDER_STYLES = `
    input[type=range]::-webkit-slider-thumb {
        pointer-events: auto;
        appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #ffffff;
        border: 2.5px solid #6366f1;
        box-shadow: 0 0 12px rgba(99,102,241,0.6), 0 2px 6px rgba(0,0,0,0.25);
        cursor: grab;
        margin-top: -6px;
        transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.15s ease;
    }
    input[type=range]::-webkit-slider-thumb:hover {
        transform: scale(1.3);
        box-shadow: 0 0 18px rgba(99,102,241,0.9), 0 4px 12px rgba(0,0,0,0.35);
    }
    input[type=range]::-webkit-slider-thumb:active {
        cursor: grabbing;
        transform: scale(1.15);
        box-shadow: 0 0 22px rgba(99,102,241,1), 0 0 0 4px rgba(99,102,241,0.25);
    }
    input[type=range]::-moz-range-thumb {
        pointer-events: auto;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #ffffff;
        border: 2.5px solid #6366f1;
        box-shadow: 0 0 12px rgba(99,102,241,0.6), 0 2px 6px rgba(0,0,0,0.25);
        cursor: grab;
        transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.15s ease;
    }
    input[type=range]::-moz-range-thumb:hover {
        transform: scale(1.3);
        box-shadow: 0 0 18px rgba(99,102,241,0.9), 0 4px 12px rgba(0,0,0,0.35);
    }
`

// PERF FIX: Stable style object reference — never recreated
const SLIDER_STYLE_HTML = { __html: SLIDER_STYLES }

// Ultra-Responsive Isolated Hero Play/Pause Button
// Decoupled from full slicer re-renders with optimistic UI response for instant 0ms feedback
export const HeroPlayPauseButton: React.FC = React.memo(() => {
    const isPlaying = usePlaybackStore((s) => s.isPlaying)
    const togglePlayback = usePlaybackStore((s) => s.togglePlayback)
    const [optimisticPlaying, setOptimisticPlaying] = useState<boolean>(isPlaying)

    // Synchronize optimistic state whenever real store state confirms
    useEffect(() => {
        setOptimisticPlaying(isPlaying)
    }, [isPlaying])

    const handleClick = useCallback(() => {
        // Immediate optimistic UI flip in 0ms!
        setOptimisticPlaying((prev) => !prev)
        // Schedule playback toggle in a non-blocking transition so the button paints immediately
        React.startTransition(() => {
            togglePlayback()
        })
    }, [togglePlayback])

    return (
        <button
            type="button"
            onClick={handleClick}
            className={`py-0.5 sm:py-1 xl:py-1.5 px-3.5 sm:px-4.5 xl:px-5 rounded-full text-white font-black text-[9.5px] sm:text-[10.5px] xl:text-[12px] transition-all duration-200 flex items-center justify-center gap-1 sm:gap-1.5 hover:scale-105 active:scale-95 shrink-0 select-none ${
                optimisticPlaying
                    ? 'bg-gradient-to-r from-rose-500 via-red-500 to-rose-600 shadow-[0_4px_16px_rgba(244,63,94,0.4)] ring-2 ring-rose-300/50 animate-pulse'
                    : 'bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 hover:from-emerald-400 hover:to-teal-500 shadow-[0_4px_16px_rgba(16,185,129,0.35)] ring-1 ring-white/30'
            }`}
            title="Play / Pause Replay (Spacebar)"
        >
            {optimisticPlaying ? (
                <>
                    <Pause className="w-2.5 h-2.5 sm:w-3 sm:h-3 xl:w-3.5 xl:h-3.5 fill-white shrink-0" />
                    <span className="tracking-wider uppercase font-black">Pause</span>
                </>
            ) : (
                <>
                    <Play className="w-2.5 h-2.5 sm:w-3 sm:h-3 xl:w-3.5 xl:h-3.5 fill-white ml-0.5 shrink-0" />
                    <span className="tracking-wider uppercase font-black">Play</span>
                </>
            )}
        </button>
    )
})
HeroPlayPauseButton.displayName = 'HeroPlayPauseButton'

export const TimeRangeSlicer: React.FC<TimeRangeSlicerProps> = React.memo(
    ({
        minMinutes = MARKET_HOURS.START_MINUTES,
        maxMinutes = MARKET_HOURS.END_MINUTES,
        onChange,
        viewMode,
        onViewModeChange,
        availableTimes = [],
        className,
        hideModeToggle = false,
        intervalMinutes = 1,
        onIntervalChange,
        forceFullDay = false,
        indexLtp = 0,
        dayChange = 0,
        dayChangeP = 0,
        symbol = '',
    }) => {
        const [startMin, setStartMin] = useState(() => minMinutes)
        const [endMin, setEndMin] = useState(() => maxMinutes)
        const [isPending, startTransition] = useTransition()
        const snapshotMin = usePlaybackStore((state) => state.snapshotMin)
        const setSnapshotMin = usePlaybackStore((state) => state.setSnapshotMin)
        const isSlicerSticky = useSettingsStore((s) => s.isSlicerSticky)
        const setIsSlicerSticky = useSettingsStore((s) => s.setIsSlicerSticky)
        const [activePreset, setActivePreset] = useState<string | null>(() => 'Day')
        const [activeQuoteIdx, setActiveQuoteIdx] = useState<number>(0)
        useEffect(() => {
            const timer = setInterval(() => {
                setActiveQuoteIdx((prev) => (prev + 1) % TRADING_PSYCHOLOGY_CONFIG.QUOTATIONS.length)
            }, 6000)
            return () => clearInterval(timer)
        }, [])

        const [isPsychologyExpanded, setIsPsychologyExpanded] = useState<boolean>(() => {
            try {
                return localStorage.getItem('slicer_psychology_expanded') === 'true'
            } catch {
                return false
            }
        })

        const togglePsychologyExpanded = useCallback(() => {
            setIsPsychologyExpanded((prev) => {
                const next = !prev
                try {
                    localStorage.setItem('slicer_psychology_expanded', String(next))
                } catch {
                    // ignore
                }
                return next
            })
        }, [])

        const [isDisciplineShieldVisible, setIsDisciplineShieldVisible] = useState<boolean>(() => {
            try {
                const stored = localStorage.getItem('slicer_discipline_shield_visible')
                return stored !== null ? stored === 'true' : true
            } catch {
                return true
            }
        })

        const toggleDisciplineShield = useCallback(() => {
            setIsDisciplineShieldVisible((prev) => {
                const next = !prev
                try {
                    localStorage.setItem('slicer_discipline_shield_visible', String(next))
                } catch {
                    // ignore
                }
                return next
            })
        }, [])

        const playSpeed = usePlaybackStore((state) => state.playSpeed)
        const setPlaySpeed = usePlaybackStore((state) => state.setPlaySpeed)
        const isPlaying = usePlaybackStore((state) => state.isPlaying)
        const setIsPlaying = usePlaybackStore((state) => state.setPlaying)
        const togglePlayback = usePlaybackStore((state) => state.togglePlayback)
        const setGlobalAvailableMinutes = usePlaybackStore((state) => state.setAvailableMinutes)
        const jumpTime = usePlaybackStore((state) => state.jumpTime)

        const isLive = useSettingsStore((s) => s.isLive)
        const storeSymbol = useSettingsStore((s) => s.symbol)
        const effectiveSymbol = symbol || storeSymbol
        const storeIndexLtp = useMarketStore((s) =>
            effectiveSymbol && s.indexLtp ? s.indexLtp[effectiveSymbol] : undefined,
        )
        const storeMarketMeta = useMarketStore((s) =>
            effectiveSymbol && s.marketMeta ? s.marketMeta[effectiveSymbol] : undefined,
        )
        const effectiveLtp = indexLtp > 0 ? indexLtp : storeIndexLtp || 0
        const effectiveDayChange = dayChange !== 0 ? dayChange : storeMarketMeta?.dayChange || 0
        const effectiveDayChangeP = dayChangeP !== 0 ? dayChangeP : storeMarketMeta?.dayChangeP || 0

        // Historical dynamic snapshot spot price & day change tracking
        const historyData = useActiveHistory(effectiveSymbol)
        const historyIndex = useMemo(() => {
            if (!historyData || historyData.length === 0) return null
            return getHistoryIndex(historyData)
        }, [historyData])

        // Active historical snapshot for current snapshotMin
        const activeHistoricalSnap = useMemo(() => {
            if (!historyIndex || historyIndex.sortedMins.length === 0) return null
            const directSnap = historyIndex.minToSnap.get(snapshotMin)
            if (directSnap) return directSnap

            // Binary search for nearest available minute
            const sorted = historyIndex.sortedMins
            let left = 0
            let right = sorted.length - 1
            let bestMin = sorted[0]!
            let bestDiff = Math.abs(bestMin - snapshotMin)

            while (left <= right) {
                const mid = (left + right) >> 1
                const m = sorted[mid]!
                const diff = Math.abs(m - snapshotMin)

                if (diff < bestDiff) {
                    bestDiff = diff
                    bestMin = m
                }

                if (m === snapshotMin) {
                    bestMin = m
                    break
                } else if (m < snapshotMin) {
                    left = mid + 1
                } else {
                    right = mid - 1
                }
            }
            return historyIndex.minToSnap.get(bestMin) || null
        }, [historyIndex, snapshotMin])

        const historicalSpot = useMemo(() => {
            if (!activeHistoricalSnap) return 0
            return extractSpotPrice(activeHistoricalSnap, effectiveSymbol)
        }, [activeHistoricalSnap, effectiveSymbol])

        // Base opening spot for historical day change (09:16 or earliest available)
        const baseSpot = useMemo(() => {
            if (!historyIndex || historyIndex.sortedMins.length === 0) return 0
            const baseMin = 556 // 09:16
            const snap = historyIndex.minToSnap.get(baseMin) || historyIndex.minToSnap.get(historyIndex.sortedMins[0]!)
            return snap ? extractSpotPrice(snap, effectiveSymbol) : 0
        }, [historyIndex, effectiveSymbol])

        const historicalDayChange = useMemo(() => {
            if (!activeHistoricalSnap) return 0
            if (
                activeHistoricalSnap.dayChange !== undefined &&
                activeHistoricalSnap.dayChange !== null &&
                activeHistoricalSnap.dayChange !== 0
            ) {
                return Number(activeHistoricalSnap.dayChange)
            }
            if (historicalSpot > 0 && baseSpot > 0) {
                return historicalSpot - baseSpot
            }
            return 0
        }, [activeHistoricalSnap, historicalSpot, baseSpot])

        const historicalDayChangeP = useMemo(() => {
            if (!activeHistoricalSnap) return 0
            if (
                activeHistoricalSnap.dayChangeP !== undefined &&
                activeHistoricalSnap.dayChangeP !== null &&
                activeHistoricalSnap.dayChangeP !== 0
            ) {
                return Number(activeHistoricalSnap.dayChangeP)
            }
            if (baseSpot > 0 && historicalDayChange !== 0) {
                return (historicalDayChange / baseSpot) * 100
            }
            return 0
        }, [activeHistoricalSnap, historicalDayChange, baseSpot])

        // In Historical mode, seamlessly bind to the active snapshot's spot price and day change
        const isHistoricalMode = viewMode === 'snapshot' || !isLive
        const displayLtp =
            isHistoricalMode && historicalSpot > 0 ? historicalSpot : effectiveLtp > 0 ? effectiveLtp : historicalSpot
        const displayDayChange = isHistoricalMode && historicalSpot > 0 ? historicalDayChange : effectiveDayChange
        const displayDayChangeP = isHistoricalMode && historicalSpot > 0 ? historicalDayChangeP : effectiveDayChangeP

        const lastLiveUpdated = useMarketStore((state) => state.lastUpdated)
        const liveLatency = useMarketStore((state) => state.liveLatency)
        const liveDataSource = useMarketStore((state) => state.liveDataSource || 'EDGE_API')

        const tierInfo = useMemo(() => {
            if (liveDataSource === 'UPSTOX_PROXY') {
                return {
                    label: 'Tier 2',
                    shortDesc: 'Proxy',
                    detail: 'Dedicated Server Proxy',
                    colorClass: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-400/40 dark:border-sky-600/50',
                    dotClass: 'bg-sky-500',
                    pingClass: 'bg-sky-400',
                }
            }
            if (liveDataSource === 'LIVE_CSV_FALLBACK') {
                return {
                    label: 'Tier 3',
                    shortDesc: 'CSV',
                    detail: 'Google Sheets Live CSV',
                    colorClass:
                        'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-400/40 dark:border-amber-600/50',
                    dotClass: 'bg-amber-500',
                    pingClass: 'bg-amber-400',
                }
            }
            // Default: Tier 1
            return {
                label: 'Tier 1',
                shortDesc: 'Edge',
                detail: 'Cloudflare Edge API',
                colorClass:
                    'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-400/40 dark:border-emerald-600/50',
                dotClass: 'bg-emerald-500',
                pingClass: 'bg-emerald-400',
            }
        }, [liveDataSource])

        const liveUpdateText = useMemo(() => {
            if (!lastLiveUpdated) return '--:--:--'
            return formatISTTime(lastLiveUpdated)
        }, [lastLiveUpdated])

        // State for the interactive time picker popover
        const [isTimePickerOpen, setIsTimePickerOpen] = useState(false)
        const [timeSearchQuery, setTimeSearchQuery] = useState('')
        const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({})
        const activeTriggerRef = useRef<HTMLButtonElement | null>(null)

        // Mode tabs inside the popover: 'dial' (Analog Clock) | 'matrix' (2-Click Grid) | 'search' (Search & Milestones)
        const [pickerTab, setPickerTabState] = useState<'dial' | 'matrix' | 'search'>(() => {
            try {
                const saved = localStorage.getItem('slicer_time_picker_tab')
                if (saved === 'dial' || saved === 'matrix' || saved === 'search') return saved
            } catch {
                // fallback
            }
            return 'dial'
        })

        const setPickerTab = useCallback((tab: 'dial' | 'matrix' | 'search') => {
            setPickerTabState(tab)
            try {
                localStorage.setItem('slicer_time_picker_tab', tab)
            } catch {
                // ignore
            }
        }, [])

        // Clock Face state (from BaseTimePicker)
        const [clockView, setClockView] = useState<'hours' | 'minutes'>('hours')
        const [clockSelectedHour, setClockSelectedHour] = useState<number>(() => Math.floor(snapshotMin / 60))
        const [clockSelectedMinute, setClockSelectedMinute] = useState<number>(() => snapshotMin % 60)
        const [isClockDragging, setIsClockDragging] = useState(false)
        const clockRef = useRef<HTMLDivElement>(null)

        // Chrono Matrix state
        const [selectedHour, setSelectedHour] = useState<number>(() => Math.floor(snapshotMin / 60))
        const [is1MinDetail, setIs1MinDetailState] = useState<boolean>(() => {
            try {
                return localStorage.getItem('slicer_time_picker_1min') === 'true'
            } catch {
                return false
            }
        })

        const setIs1MinDetail = useCallback((val: boolean | ((prev: boolean) => boolean)) => {
            setIs1MinDetailState((prev) => {
                const next = typeof val === 'function' ? val(prev) : val
                try {
                    localStorage.setItem('slicer_time_picker_1min', String(next))
                } catch {
                    // ignore
                }
                return next
            })
        }, [])

        // Function to calculate popover position relative to the clicked trigger
        const updatePopoverPosition = useCallback((triggerEl?: HTMLButtonElement | null) => {
            const el = triggerEl || activeTriggerRef.current
            if (!el) return

            const rect = el.getBoundingClientRect()
            const popoverWidth = window.innerWidth < 400 ? 320 : 360
            const popoverHeight = 440

            let left = rect.right - popoverWidth
            let top = rect.bottom + 8

            // Prevent off-screen left
            if (left < 12) left = 12
            // Prevent off-screen right
            if (left + popoverWidth > window.innerWidth - 12) {
                left = window.innerWidth - popoverWidth - 12
            }

            // Prevent off-screen bottom: open upwards if needed
            if (top + popoverHeight > window.innerHeight - 12) {
                if (rect.top - popoverHeight - 8 > 12) {
                    top = rect.top - popoverHeight - 8
                } else {
                    top = Math.max(12, window.innerHeight - popoverHeight - 12)
                }
            }

            setPopoverStyle({
                position: 'fixed',
                top: `${top}px`,
                left: `${left}px`,
                width: `${popoverWidth}px`,
                zIndex: 99999,
            })
        }, [])

        // Sync clock and matrix state whenever snapshotMin changes or popover opens
        useEffect(() => {
            if (isTimePickerOpen) {
                const h = Math.floor(snapshotMin / 60)
                const m = snapshotMin % 60
                setClockSelectedHour(h)
                setClockSelectedMinute(m)
                setSelectedHour(h)
                setClockView('hours')
                updatePopoverPosition()
            }
        }, [isTimePickerOpen, snapshotMin, updatePopoverPosition])

        // Robust click-outside & window resize handler
        useEffect(() => {
            const handleClickOutside = (e: MouseEvent) => {
                const target = e.target as Element
                if (!target) return
                // Don't close if clicking anywhere inside the popover or on any trigger button
                if (target.closest('.slicer-time-picker-popover') || target.closest('.slicer-time-picker-trigger')) {
                    return
                }
                setIsTimePickerOpen(false)
            }

            const handleEsc = (e: KeyboardEvent) => {
                if (e.key === 'Escape') setIsTimePickerOpen(false)
            }

            const handleReposition = () => {
                if (isTimePickerOpen) updatePopoverPosition()
            }

            if (isTimePickerOpen) {
                document.addEventListener('mousedown', handleClickOutside)
                document.addEventListener('keydown', handleEsc)
                window.addEventListener('resize', handleReposition)
                window.addEventListener('scroll', handleReposition, true)
            }
            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
                document.removeEventListener('keydown', handleEsc)
                window.removeEventListener('resize', handleReposition)
                window.removeEventListener('scroll', handleReposition, true)
            }
        }, [isTimePickerOpen, updatePopoverPosition])

        // --- Clock Radial Math Logic (BaseTimePicker Engine) ---
        const handleClockInteraction = useCallback(
            (clientX: number, clientY: number) => {
                if (!clockRef.current) return
                const rect = clockRef.current.getBoundingClientRect()
                const CLOCK_RADIUS = 100
                const x = clientX - rect.left - CLOCK_RADIUS
                const y = clientY - rect.top - CLOCK_RADIUS
                let angle = Math.atan2(y, x) * (180 / Math.PI) + 90
                if (angle < 0) angle += 360

                if (clockView === 'hours') {
                    let hour = Math.round(angle / 30)
                    if (hour === 0) hour = 12
                    const minH = Math.floor(minMinutes / 60)
                    const maxH = Math.floor(maxMinutes / 60)
                    const pmSpan = maxH > 12 ? maxH - 12 : 3
                    if (hour >= 1 && hour <= pmSpan) hour += 12

                    // Market hours clamp
                    if (hour < minH) hour = minH
                    if (hour > maxH) hour = maxH

                    setClockSelectedHour(hour)
                    setSelectedHour(hour)
                } else {
                    let minute = Math.round(angle / 6)
                    if (minute === 60) minute = 0

                    // Check boundaries
                    const minH = Math.floor(minMinutes / 60)
                    const maxH = Math.floor(maxMinutes / 60)
                    const minM = minMinutes % 60
                    const maxM = maxMinutes % 60
                    if (clockSelectedHour === minH && minute < minM) minute = minM
                    if (clockSelectedHour === maxH && minute > maxM) minute = maxM

                    setClockSelectedMinute(minute)
                }
            },
            [clockView, clockSelectedHour, minMinutes, maxMinutes],
        )

        useEffect(() => {
            if (isClockDragging) {
                const handleMouseMove = (e: MouseEvent) => {
                    e.preventDefault()
                    handleClockInteraction(e.clientX, e.clientY)
                }
                const handleMouseUp = () => {
                    setIsClockDragging(false)
                    if (clockView === 'hours') {
                        setTimeout(() => setClockView('minutes'), 220)
                    }
                }
                window.addEventListener('mousemove', handleMouseMove)
                window.addEventListener('mouseup', handleMouseUp)
                return () => {
                    window.removeEventListener('mousemove', handleMouseMove)
                    window.removeEventListener('mouseup', handleMouseUp)
                }
            }
        }, [isClockDragging, handleClockInteraction, clockView])

        const CLOCK_SIZE = 200
        const NUMBER_RADIUS = 76
        const activeAngle = clockView === 'hours' ? (clockSelectedHour % 12) * 30 : clockSelectedMinute * 6

        // Render Clock Face Numbers
        const renderClockNumbers = () => {
            let items: number[] = []
            if (clockView === 'hours') {
                const minH = Math.floor(minMinutes / 60)
                const maxH = Math.floor(maxMinutes / 60)
                for (let i = minH; i <= maxH; i++) items.push(i)
            } else {
                items = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]
            }

            return items.map((val) => {
                let angle = clockView === 'hours' ? (val % 12) * 30 : val * 6
                const display =
                    clockView === 'hours' ? (val > 12 ? `${val - 12}P` : `${val}A`) : `:${String(val).padStart(2, '0')}`
                const rad = (angle - 90) * (Math.PI / 180)
                const x = Math.cos(rad) * NUMBER_RADIUS
                const y = Math.sin(rad) * NUMBER_RADIUS

                const isActive = clockView === 'hours' ? clockSelectedHour === val : clockSelectedMinute === val

                return (
                    <div
                        key={val}
                        className={`absolute top-1/2 left-1/2 w-7 h-7 flex items-center justify-center text-[11px] font-mono font-black rounded-full pointer-events-none transition-colors duration-200 z-20 ${
                            isActive ? 'text-white' : 'text-slate-700 dark:text-slate-200'
                        }`}
                        style={{ transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}
                    >
                        {display}
                    </div>
                )
            })
        }

        // When forceFullDay changes to true (e.g. user switches to Historical mode), reset slicer
        useEffect(() => {
            if (forceFullDay) {
                setStartMin(minMinutes)
                setEndMin(maxMinutes)
                setSnapshotMin(maxMinutes)
                setActivePreset('Day')
                usePlaybackStore.getState().setPlaying(false)
            }
        }, [forceFullDay, minMinutes, maxMinutes, setSnapshotMin])

        // Available time indices for step-through
        const availableMinutes = useMemo(() => {
            if (!availableTimes?.length) return []
            const mins = availableTimes
                .map((t) => {
                    const [h, m] = t.split(':').map(Number)
                    return (h || 0) * 60 + (m || 0)
                })
                .filter((m) => m >= minMinutes && m <= maxMinutes)
                .sort((a, b) => a - b)

            return Array.from(new Set(mins))
        }, [availableTimes, minMinutes, maxMinutes])

        useEffect(() => {
            setGlobalAvailableMinutes(availableMinutes)
        }, [availableMinutes, setGlobalAvailableMinutes])

        // Memoize reversed list for fast dropdown rendering
        const reversedAvailableMinutes = useMemo(() => {
            return availableMinutes.length > 0 ? [...availableMinutes].reverse() : []
        }, [availableMinutes])

        // Snap slider value to nearest available time
        const snapToNearest = useCallback(
            (val: number) => {
                if (availableMinutes.length === 0) return val
                let bestIdx = 0
                let bestDiff = Math.abs(availableMinutes[0]! - val)
                for (let i = 1; i < availableMinutes.length; i++) {
                    const diff = Math.abs(availableMinutes[i]! - val)
                    if (diff < bestDiff) {
                        bestDiff = diff
                        bestIdx = i
                    }
                }
                return availableMinutes[bestIdx]!
            },
            [availableMinutes],
        )

        // Centralized Market Trading Hours definition
        const MARKET_HOURS_LIST = useMemo(
            () => MARKET_HOURS.HOURS_LIST,
            [],
        )

        // Session helper for active snapshot time
        const activeSessionInfo = useMemo(() => {
            if (snapshotMin < 570)
                return {
                    label: 'Opening Bell',
                    icon: '🔔',
                    color: 'text-amber-500 bg-amber-500/10 border-amber-500/30',
                }
            if (snapshotMin < 660)
                return {
                    label: 'Morning Trend',
                    icon: '⚡',
                    color: 'text-indigo-500 bg-indigo-500/10 border-indigo-500/30',
                }
            if (snapshotMin < 750)
                return {
                    label: 'Pre-Noon Liquidity',
                    icon: '📈',
                    color: 'text-blue-500 bg-blue-500/10 border-blue-500/30',
                }
            if (snapshotMin < 810)
                return {
                    label: 'Mid-Day Consolidation',
                    icon: '🥪',
                    color: 'text-teal-500 bg-teal-500/10 border-teal-500/30',
                }
            if (snapshotMin < 870)
                return {
                    label: 'European Crossover',
                    icon: '🌍',
                    color: 'text-purple-500 bg-purple-500/10 border-purple-500/30',
                }
            return {
                label: 'Closing Phase',
                icon: '🎯',
                color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30',
            }
        }, [snapshotMin])

        // Centralized key market milestone jump times
        const KEY_MARKET_TIMES = useMemo(
            () => MARKET_HOURS.KEY_MILESTONES,
            [],
        )

        // Minutes for the selected hour in Chrono Matrix
        const minuteListForSelectedHour = useMemo(() => {
            if (is1MinDetail) {
                return Array.from({ length: 60 }, (_, i) => i)
            }
            return [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]
        }, [is1MinDetail])

        // Filtered available minutes for search tab
        const filteredSearchList = useMemo(() => {
            let list = reversedAvailableMinutes.length > 0 ? reversedAvailableMinutes : [snapshotMin]
            if (timeSearchQuery.trim()) {
                const query = timeSearchQuery.trim().toLowerCase()
                list = list.filter((m) => {
                    const formatted = formatMinutes(m).toLowerCase()
                    const raw24 = toTimeString(m).toLowerCase()
                    return formatted.includes(query) || raw24.includes(query)
                })
            }
            return list
        }, [reversedAvailableMinutes, snapshotMin, timeSearchQuery])

        // Commit clock changes to snapshotMin
        const handleClockConfirm = useCallback(() => {
            let h = clockSelectedHour
            let m = clockSelectedMinute
            let total = h * 60 + m
            if (total < minMinutes) total = minMinutes
            if (total > maxMinutes) total = maxMinutes

            setSnapshotMin(snapToNearest(total))
            setActivePreset(null)
            setIsPlaying(false)
            setIsTimePickerOpen(false)
        }, [clockSelectedHour, clockSelectedMinute, snapToNearest, setSnapshotMin, setIsPlaying, minMinutes, maxMinutes])

        // Interactive Time Picker Popover Sub-Component
        const renderInteractiveTimePicker = (sizeClasses: string) => {
            if (viewMode === 'comparison') {
                return (
                    <div
                        className={`${sizeClasses} shrink-0 font-mono font-bold tabular-nums text-slate-700 dark:text-slate-200 rounded-full border border-slate-200/90 dark:border-slate-700/70 bg-white/90 dark:bg-slate-800/90 shadow-xs flex items-center justify-center`}
                    >
                        <span className="px-1">
                            {formatMinutes(startMin)} → {formatMinutes(endMin)}
                        </span>
                    </div>
                )
            }

            return (
                <div className="relative inline-flex items-center">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation()
                            const nextState = !isTimePickerOpen
                            if (nextState) {
                                activeTriggerRef.current = e.currentTarget
                                updatePopoverPosition(e.currentTarget)
                            }
                            setIsTimePickerOpen(nextState)
                        }}
                        className={`slicer-time-picker-trigger ${sizeClasses} shrink-0 font-black rounded-full border tracking-wide flex items-center justify-center gap-1.5 whitespace-nowrap transition-all duration-200 cursor-pointer ${
                            isPlaying
                                ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700/50 shadow-emerald-500/10'
                                : isTimePickerOpen
                                  ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white border-teal-600 shadow-md shadow-emerald-500/30 ring-2 ring-emerald-400/40'
                                  : 'bg-white/95 dark:bg-slate-800/95 text-slate-800 dark:text-slate-100 border-slate-200/90 dark:border-slate-700/70 hover:border-emerald-400 shadow-xs'
                        }`}
                        title="Click to Jump to Time"
                    >
                        {isPlaying ? (
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping shrink-0" />
                        ) : (
                            <Clock className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400 shrink-0" />
                        )}
                        <span className="font-mono font-bold tracking-tight">{formatMinutes(snapshotMin)}</span>
                        <ChevronDown
                            className={`w-3 h-3 transition-transform duration-200 ${isTimePickerOpen ? 'rotate-180 text-white' : 'text-slate-400'}`}
                        />
                    </button>

                    {/* Floating Ultra-Premium Chronograph Portal Popover */}
                    {isTimePickerOpen &&
                        typeof document !== 'undefined' &&
                        createPortal(
                            <div
                                className="slicer-time-picker-popover bg-gradient-to-b from-white/98 via-white/95 to-slate-50/95 dark:from-slate-900/98 dark:via-slate-900/95 dark:to-slate-950/95 backdrop-blur-3xl rounded-3xl border border-slate-200/90 dark:border-slate-800/90 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35)] dark:shadow-[0_24px_60px_-12px_rgba(0,0,0,0.9)] p-3.5 flex flex-col gap-2.5 select-none animate-in fade-in zoom-in-95 duration-150"
                                style={popoverStyle}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* 1. Header Cockpit: Digital Dual Readout + Session Pill + Rapid Steppers */}
                                <div className="flex items-center justify-between bg-slate-100/90 dark:bg-slate-800/90 p-2 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-2xs">
                                    <div className="flex items-center gap-2">
                                        <div className="flex flex-col">
                                            <div className="flex items-center gap-1.5">
                                                <Clock className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                                <span className="font-mono text-base font-black text-slate-900 dark:text-white tabular-nums tracking-tight">
                                                    {formatMinutes(snapshotMin)}
                                                </span>
                                            </div>
                                            <span
                                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider border ${activeSessionInfo.color}`}
                                            >
                                                <span>{activeSessionInfo.icon}</span>
                                                <span>{activeSessionInfo.label}</span>
                                            </span>
                                        </div>
                                    </div>

                                    {/* Rapid Step Nudge Buttons */}
                                    <div className="flex items-center gap-0.5 bg-white/90 dark:bg-slate-700/90 p-0.5 rounded-xl border border-slate-200/80 dark:border-slate-600/80">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                jumpTime(-15)
                                            }}
                                            className="px-1.5 py-1 rounded-lg text-[9px] font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 hover:text-slate-900 transition-colors"
                                            title="Jump -15m"
                                        >
                                            -15m
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                jumpTime(-5)
                                            }}
                                            className="px-1.5 py-1 rounded-lg text-[9px] font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 hover:text-slate-900 transition-colors"
                                            title="Jump -5m"
                                        >
                                            -5m
                                        </button>
                                        <div className="w-px h-3 bg-slate-300 dark:bg-slate-600" />
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                jumpTime(5)
                                            }}
                                            className="px-1.5 py-1 rounded-lg text-[9px] font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 hover:text-slate-900 transition-colors"
                                            title="Jump +5m"
                                        >
                                            +5m
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                jumpTime(15)
                                            }}
                                            className="px-1.5 py-1 rounded-lg text-[9px] font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 hover:text-slate-900 transition-colors"
                                            title="Jump +15m"
                                        >
                                            +15m
                                        </button>
                                    </div>
                                </div>

                                {/* 2. Top-Level Mode Tabs */}
                                <div className="flex items-center justify-between gap-1 bg-slate-100/90 dark:bg-slate-800/90 p-0.5 rounded-xl border border-slate-200/70 dark:border-slate-700/60 text-[9.5px] font-black">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setPickerTab('dial')
                                        }}
                                        className={`flex-1 py-1 rounded-lg transition-all flex items-center justify-center gap-1 ${
                                            pickerTab === 'dial'
                                                ? 'bg-white dark:bg-slate-700 text-teal-700 dark:text-teal-300 shadow-2xs font-extrabold'
                                                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'
                                        }`}
                                    >
                                        <Clock className="w-3 h-3 text-teal-500" />
                                        <span>Clock Dial</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setPickerTab('matrix')
                                        }}
                                        className={`flex-1 py-1 rounded-lg transition-all flex items-center justify-center gap-1 ${
                                            pickerTab === 'matrix'
                                                ? 'bg-white dark:bg-slate-700 text-emerald-700 dark:text-emerald-300 shadow-2xs font-extrabold'
                                                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'
                                        }`}
                                    >
                                        <Sparkles className="w-3 h-3 text-emerald-500" />
                                        <span>2-Click Matrix</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setPickerTab('search')
                                        }}
                                        className={`flex-1 py-1 rounded-lg transition-all flex items-center justify-center gap-1 ${
                                            pickerTab === 'search'
                                                ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-2xs font-extrabold'
                                                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'
                                        }`}
                                    >
                                        <Search className="w-3 h-3 text-indigo-500" />
                                        <span>Search</span>
                                    </button>
                                </div>

                                {/* TAB 1: Swiss Radial Clock Face (From BaseTimePicker) */}
                                {pickerTab === 'dial' && (
                                    <div className="flex flex-col items-center gap-2">
                                        {/* Dual Flip Block: [ HOUR ] : [ MINUTE ] */}
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setClockView('hours')
                                                }}
                                                className={`px-3 py-1.5 flex flex-col items-center rounded-xl transition-all border-2 ${
                                                    clockView === 'hours'
                                                        ? 'bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 border-teal-500 shadow-xs'
                                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                                                }`}
                                            >
                                                <span className="text-xl font-black font-mono tracking-tight leading-none">
                                                    {String(clockSelectedHour).padStart(2, '0')}
                                                </span>
                                                <span className="text-[7.5px] uppercase font-bold text-slate-400">
                                                    Hour
                                                </span>
                                            </button>

                                            <span className="text-lg font-black text-slate-400">:</span>

                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setClockView('minutes')
                                                }}
                                                className={`px-3 py-1.5 flex flex-col items-center rounded-xl transition-all border-2 ${
                                                    clockView === 'minutes'
                                                        ? 'bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 border-teal-500 shadow-xs'
                                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                                                }`}
                                            >
                                                <span className="text-xl font-black font-mono tracking-tight leading-none">
                                                    {String(clockSelectedMinute).padStart(2, '0')}
                                                </span>
                                                <span className="text-[7.5px] uppercase font-bold text-slate-400">
                                                    Minute
                                                </span>
                                            </button>

                                            <div className="ml-1 pl-2 border-l border-slate-200 dark:border-slate-700 flex flex-col">
                                                <span className="text-[11px] font-black text-teal-600 dark:text-teal-400 font-mono">
                                                    {clockSelectedHour >= 12 ? 'PM' : 'AM'}
                                                </span>
                                                <span className="text-[7.5px] font-extrabold text-slate-400 uppercase">
                                                    {clockView === 'hours' ? 'Pick Hour' : 'Pick Minute'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Circular Clock Dial */}
                                        <div className="flex justify-center items-center relative py-1">
                                            <div
                                                ref={clockRef}
                                                className="relative rounded-full bg-slate-100/70 dark:bg-slate-800/70 shadow-inner border border-slate-200/60 dark:border-slate-700/60 cursor-pointer touch-none select-none"
                                                style={{ width: CLOCK_SIZE, height: CLOCK_SIZE }}
                                                onMouseDown={(e) => {
                                                    e.stopPropagation()
                                                    setIsClockDragging(true)
                                                    handleClockInteraction(e.clientX, e.clientY)
                                                }}
                                            >
                                                {/* Center Dot */}
                                                <div className="absolute top-1/2 left-1/2 w-2.5 h-2.5 -ml-1.25 -mt-1.25 bg-teal-500 rounded-full z-20 shadow-xs" />

                                                {/* Clock Hand */}
                                                <div
                                                    className={`absolute bottom-1/2 left-1/2 w-[2px] bg-teal-500 pointer-events-none z-10 ${isClockDragging ? '' : 'transition-transform duration-200 ease-out'}`}
                                                    style={{
                                                        height: NUMBER_RADIUS,
                                                        transformOrigin: 'bottom center',
                                                        transform: `translateX(-50%) rotate(${activeAngle}deg)`,
                                                    }}
                                                >
                                                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[30px] h-[30px] bg-teal-500 rounded-full flex items-center justify-center shadow-md shadow-teal-500/40">
                                                        {clockView === 'minutes' && clockSelectedMinute % 5 !== 0 && (
                                                            <div className="w-1.5 h-1.5 bg-white rounded-full" />
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Clock Numbers */}
                                                {renderClockNumbers()}
                                            </div>
                                        </div>

                                        {/* Confirm Button */}
                                        <div className="w-full flex items-center gap-2 pt-1">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setIsTimePickerOpen(false)
                                                }}
                                                className="flex-1 py-2 rounded-xl text-[11px] font-black text-slate-500 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleClockConfirm()
                                                }}
                                                className="flex-1 py-2 rounded-xl text-[11px] font-black text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 shadow-md shadow-emerald-500/30 transition-all active:scale-95 flex items-center justify-center gap-1"
                                            >
                                                <Check className="w-3.5 h-3.5" />
                                                <span>Set Time</span>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* TAB 2: Chrono Matrix (Hour & Minute Grid) */}
                                {pickerTab === 'matrix' && (
                                    <div className="flex flex-col gap-2">
                                        {/* 1. Hour Selector Strip */}
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center justify-between px-0.5">
                                                <span className="text-[8.5px] uppercase font-black tracking-wider text-slate-400 dark:text-slate-500">
                                                    1. Select Hour
                                                </span>
                                                <span className="text-[9px] font-extrabold text-emerald-600 dark:text-emerald-400">
                                                    {MARKET_HOURS_LIST.find((h) => h.hour === selectedHour)?.session} Session
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-4 sm:grid-cols-8 gap-1">
                                                {MARKET_HOURS_LIST.map((h) => {
                                                    const isHourActive = selectedHour === h.hour
                                                    const currentHourOfSnapshot =
                                                        Math.floor(snapshotMin / 60) === h.hour
                                                    return (
                                                        <button
                                                            key={h.hour}
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setSelectedHour(h.hour)
                                                            }}
                                                            className={`py-1.5 rounded-xl text-center flex flex-col items-center justify-center transition-all duration-150 ${
                                                                isHourActive
                                                                    ? 'bg-gradient-to-b from-teal-600 to-emerald-600 text-white shadow-md shadow-emerald-500/30 ring-2 ring-emerald-400 scale-[1.03]'
                                                                    : currentHourOfSnapshot
                                                                      ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700/50'
                                                                      : 'bg-slate-100/90 dark:bg-slate-800/90 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                                            }`}
                                                        >
                                                            <span className="text-[10px] font-mono font-black">
                                                                {h.label.split(' ')[0]}
                                                            </span>
                                                            <span
                                                                className={`text-[7px] font-extrabold uppercase ${isHourActive ? 'text-emerald-100' : 'text-slate-400'}`}
                                                            >
                                                                {h.label.split(' ')[1]}
                                                            </span>
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        </div>

                                        {/* 2. Minute Grid for Selected Hour */}
                                        <div className="flex flex-col gap-1 bg-slate-50 dark:bg-slate-800/40 p-2 rounded-2xl border border-slate-100 dark:border-slate-800">
                                            <div className="flex items-center justify-between px-0.5">
                                                <span className="text-[8.5px] uppercase font-black tracking-wider text-slate-400 dark:text-slate-500">
                                                    2. Select Minute (
                                                    {selectedHour > 12 ? selectedHour - 12 : selectedHour}{' '}
                                                    {selectedHour >= 12 ? 'PM' : 'AM'})
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setIs1MinDetail(!is1MinDetail)
                                                    }}
                                                    className="text-[8.5px] font-black text-teal-600 dark:text-teal-400 hover:underline cursor-pointer"
                                                >
                                                    {is1MinDetail ? 'Show 5m Major' : 'Show All 60 Mins'}
                                                </button>
                                            </div>

                                            {/* Minute Chips Grid */}
                                            <div
                                                className={`grid ${is1MinDetail ? 'grid-cols-6 sm:grid-cols-10 max-h-36 overflow-y-auto custom-scrollbar' : 'grid-cols-4 sm:grid-cols-6'} gap-1`}
                                            >
                                                {minuteListForSelectedHour.map((min) => {
                                                    const totalMin = selectedHour * 60 + min
                                                    const isValid = totalMin >= minMinutes && totalMin <= maxMinutes
                                                    const isSelected = snapshotMin === totalMin
                                                    return (
                                                        <button
                                                            key={min}
                                                            type="button"
                                                            disabled={!isValid}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                if (!isValid) return
                                                                setSnapshotMin(snapToNearest(totalMin))
                                                                setActivePreset(null)
                                                                setIsPlaying(false)
                                                                setIsTimePickerOpen(false)
                                                            }}
                                                            className={`py-1.5 px-1 rounded-xl text-center font-mono transition-all duration-150 ${
                                                                !isValid
                                                                    ? 'opacity-25 cursor-not-allowed bg-slate-100 dark:bg-slate-800/50 text-slate-400 text-[9px]'
                                                                    : isSelected
                                                                      ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-black shadow-md shadow-emerald-500/30 ring-2 ring-emerald-400 text-[10.5px] scale-105'
                                                                      : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/60 hover:text-emerald-600 hover:border-emerald-300 font-bold border border-slate-200/70 dark:border-slate-700/60 text-[10px] shadow-2xs hover:scale-105 active:scale-95'
                                                            }`}
                                                        >
                                                            :{String(min).padStart(2, '0')}
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* TAB 3: Search & All Snapshots */}
                                {pickerTab === 'search' && (
                                    <div className="flex flex-col gap-2">
                                        <div className="relative flex items-center bg-slate-100/90 dark:bg-slate-800/90 rounded-xl px-2.5 py-1.5 border border-slate-200/80 dark:border-slate-700/80 focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-500/20 transition-all">
                                            <Search className="w-3.5 h-3.5 text-slate-400 shrink-0 mr-1.5" />
                                            <input
                                                type="text"
                                                value={timeSearchQuery}
                                                onChange={(e) => setTimeSearchQuery(e.target.value)}
                                                placeholder="Jump to time (e.g. 10:15, 2:30)..."
                                                className="w-full bg-transparent text-[11px] font-bold text-slate-800 dark:text-slate-100 placeholder-slate-400 outline-none"
                                                autoFocus
                                            />
                                            {timeSearchQuery && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setTimeSearchQuery('')
                                                    }}
                                                    className="p-0.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-4 sm:grid-cols-5 gap-1 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                                            {filteredSearchList.map((min) => {
                                                const isSelected = snapshotMin === min
                                                return (
                                                    <button
                                                        key={min}
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            setSnapshotMin(min)
                                                            setActivePreset(null)
                                                            setIsPlaying(false)
                                                            setIsTimePickerOpen(false)
                                                        }}
                                                        className={`py-1 px-1 rounded-lg text-[10px] font-mono font-black transition-all flex items-center justify-center gap-0.5 ${
                                                            isSelected
                                                                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-xs ring-2 ring-emerald-400/50'
                                                                : 'bg-slate-100/70 dark:bg-slate-800/70 text-slate-700 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/60 hover:text-emerald-600 hover:border-emerald-200'
                                                        }`}
                                                    >
                                                        {formatMinutes(min).replace(' ', '')}
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* 3. Market Session Milestones Quick Jump Strip */}
                                <div className="flex flex-col gap-1 pt-1 border-t border-slate-100 dark:border-slate-800/80">
                                    <span className="text-[8.5px] uppercase font-black tracking-wider text-slate-400 dark:text-slate-500 px-0.5">
                                        Quick Session Milestones
                                    </span>
                                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
                                        {KEY_MARKET_TIMES.map((mark) => {
                                            const isCurrent = snapshotMin === mark.min
                                            return (
                                                <button
                                                    key={mark.min}
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setSnapshotMin(mark.min)
                                                        setActivePreset(null)
                                                        setIsPlaying(false)
                                                        setIsTimePickerOpen(false)
                                                    }}
                                                    className={`px-1.5 py-1 rounded-lg text-[8.5px] font-black whitespace-nowrap transition-all duration-150 text-center ${
                                                        isCurrent
                                                            ? 'bg-emerald-600 text-white shadow-xs'
                                                            : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/60 hover:text-emerald-600'
                                                    }`}
                                                >
                                                    {mark.badge}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>,
                            document.body,
                        )}
                </div>
            )
        }

        // Master Playback Deck renderer (shared between desktop unified row and tablet replay row)
        const renderPlaybackDeck = () => (
            <>
                {/* Left Jump Cluster */}
                <div className="flex items-center gap-0.5 sm:gap-1 pr-1 border-r border-slate-300/80 dark:border-slate-700/80">
                    <button
                        onClick={() => {
                            setSnapshotMin(availableMinutes[0] || minMinutes)
                            setActivePreset(null)
                        }}
                        className="px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-white/80 hover:bg-white dark:bg-slate-700/80 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[9px] sm:text-[10px] xl:text-[11px] font-extrabold shadow-2xs transition-all hover:scale-105 active:scale-95 shrink-0"
                        title="Jump to Start (Home)"
                    >
                        Start
                    </button>
                    <button
                        onClick={() => {
                            React.startTransition(() => {
                                jumpTime(-15)
                                setActivePreset(null)
                            })
                        }}
                        className="px-1 sm:px-1.5 py-0.5 sm:py-1 rounded-full hover:bg-white/80 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 text-[9px] sm:text-[10px] xl:text-[11px] font-bold transition-all hover:scale-105 active:scale-95 shrink-0"
                        title="Rewind 15 minutes"
                    >
                        ≪15m
                    </button>
                    <button
                        onClick={() => {
                            React.startTransition(() => {
                                jumpTime(-5)
                                setActivePreset(null)
                            })
                        }}
                        className="px-1 sm:px-1.5 py-0.5 sm:py-1 rounded-full hover:bg-white/80 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 text-[9px] sm:text-[10px] xl:text-[11px] font-bold transition-all hover:scale-105 active:scale-95 shrink-0"
                        title="Rewind 5 minutes (Shift+Left)"
                    >
                        ≪5m
                    </button>
                    <button
                        onClick={() => {
                            React.startTransition(() => {
                                jumpTime(-1)
                                setActivePreset(null)
                            })
                        }}
                        className="px-1 sm:px-1.5 py-0.5 sm:py-1 rounded-full hover:bg-white/80 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 text-[9px] sm:text-[10px] xl:text-[11px] font-bold transition-all hover:scale-105 active:scale-95 shrink-0"
                        title="Step back 1 minute (Left Arrow)"
                    >
                        ≪1m
                    </button>
                </div>

                {/* Central Hero Play / Pause Button with Radiant Glow */}
                <div className="flex items-center px-1 sm:px-1.5">
                    <HeroPlayPauseButton />
                </div>

                {/* Right Jump Cluster */}
                <div className="flex items-center gap-0.5 sm:gap-1 pl-1 border-l border-slate-300/80 dark:border-slate-700/80">
                    <button
                        onClick={() => {
                            React.startTransition(() => {
                                jumpTime(1)
                                setActivePreset(null)
                            })
                        }}
                        className="px-1 sm:px-1.5 py-0.5 sm:py-1 rounded-full hover:bg-white/80 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 text-[9px] sm:text-[10px] xl:text-[11px] font-bold transition-all hover:scale-105 active:scale-95 shrink-0"
                        title="Step forward 1 minute (Right Arrow)"
                    >
                        1m≫
                    </button>
                    <button
                        onClick={() => {
                            React.startTransition(() => {
                                jumpTime(5)
                                setActivePreset(null)
                            })
                        }}
                        className="px-1 sm:px-1.5 py-0.5 sm:py-1 rounded-full hover:bg-white/80 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 text-[9px] sm:text-[10px] xl:text-[11px] font-bold transition-all hover:scale-105 active:scale-95 shrink-0"
                        title="Fast forward 5 minutes (Shift+Right)"
                    >
                        5m≫
                    </button>
                    <button
                        onClick={() => {
                            React.startTransition(() => {
                                jumpTime(15)
                                setActivePreset(null)
                            })
                        }}
                        className="px-1 sm:px-1.5 py-0.5 sm:py-1 rounded-full hover:bg-white/80 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 text-[9px] sm:text-[10px] xl:text-[11px] font-bold transition-all hover:scale-105 active:scale-95 shrink-0"
                        title="Fast forward 15 minutes"
                    >
                        15m≫
                    </button>
                    <button
                        onClick={() => {
                            setSnapshotMin(availableMinutes[availableMinutes.length - 1] || maxMinutes)
                            setActivePreset('Day')
                        }}
                        className="px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-white/80 hover:bg-white dark:bg-slate-700/80 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[9px] sm:text-[10px] xl:text-[11px] font-extrabold shadow-2xs transition-all hover:scale-105 active:scale-95 shrink-0"
                        title="Jump to End of Day (End)"
                    >
                        End
                    </button>
                </div>

                {/* Speed Controller with Slowdown (-) and Speedup (+) controls */}
                <div className="w-px h-3.5 xl:h-4 bg-slate-300 dark:bg-slate-700 mx-0.5 sm:mx-1"></div>
                <div className="flex items-center gap-0.5 bg-white/90 dark:bg-slate-700/80 rounded-full px-1 py-0.5 border border-slate-200/80 dark:border-slate-600/70 shadow-2xs shrink-0 select-none">
                    {/* Slow Down Button (-) */}
                    <button
                        type="button"
                        onClick={() => {
                            const idx = SPEEDS.indexOf(playSpeed as any)
                            if (idx > 0) {
                                setPlaySpeed(SPEEDS[idx - 1]!)
                            }
                        }}
                        disabled={SPEEDS.indexOf(playSpeed as any) <= 0}
                        className="w-4 h-4 sm:w-4.5 sm:h-4.5 rounded-full flex items-center justify-center text-slate-500 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-600 active:scale-90 disabled:opacity-25 disabled:cursor-not-allowed transition-all text-[11px] font-black shrink-0 cursor-pointer"
                        title="Slow Down Playback (−)"
                    >
                        −
                    </button>

                    {/* Speed Dropdown Badge */}
                    <div className="relative flex items-center shrink-0">
                        <select
                            value={playSpeed}
                            onChange={(e) => setPlaySpeed(Number(e.target.value) as typeof playSpeed)}
                            className="appearance-none bg-transparent outline-none pl-0.5 pr-3.5 xl:pr-4 py-0.5 text-[9px] sm:text-[10px] xl:text-[11px] font-black cursor-pointer text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 transition-colors z-10 relative"
                            title={`Playback Speed: ${PLAYBACK_SPEED_LABELS[playSpeed] || `${playSpeed}x`} (Click to select)`}
                        >
                            {SPEEDS.map((speed) => (
                                <option
                                    key={speed}
                                    value={speed}
                                    className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800 font-bold"
                                >
                                    {PLAYBACK_SPEED_LABELS[speed] || `${speed}x`}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="w-2.5 h-2.5 absolute right-0.5 pointer-events-none text-indigo-500/80" />
                    </div>

                    {/* Speed Up Button (+) */}
                    <button
                        type="button"
                        onClick={() => {
                            const idx = SPEEDS.indexOf(playSpeed as any)
                            if (idx >= 0 && idx < SPEEDS.length - 1) {
                                setPlaySpeed(SPEEDS[idx + 1]!)
                            }
                        }}
                        disabled={SPEEDS.indexOf(playSpeed as any) >= SPEEDS.length - 1}
                        className="w-4 h-4 sm:w-4.5 sm:h-4.5 rounded-full flex items-center justify-center text-slate-500 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-600 active:scale-90 disabled:opacity-25 disabled:cursor-not-allowed transition-all text-[11px] font-black shrink-0 cursor-pointer"
                        title="Speed Up Playback (+)"
                    >
                        +
                    </button>
                </div>
            </>
        )

        // ─── ZERO-LATENCY INSTANT TIME DISPATCHER ──────────────────────────────
        // Dispatches time updates synchronously on tick without multi-frame queuing delays
        const lastDispatchedRef = useRef<string>('')
        const wasPlayingRef = useRef<boolean>(isPlaying)

        // Ensure playback automatically switches to snapshot (History) mode if needed
        useEffect(() => {
            if (isPlaying && viewMode !== 'snapshot') {
                onViewModeChange('snapshot')
            }
        }, [isPlaying, viewMode, onViewModeChange])

        useEffect(() => {
            let newStart = '',
                newEnd = ''
            if (viewMode === 'snapshot' || !isLive || isPlaying) {
                newStart = toTimeString(snapshotMin)
                newEnd = toTimeString(snapshotMin)
            } else {
                newStart = toTimeString(startMin)
                newEnd = toTimeString(endMin)
            }
            const key = `${newStart}-${newEnd}`
            
            const wasPlaying = wasPlayingRef.current
            wasPlayingRef.current = isPlaying
            const isPauseEvent = wasPlaying && !isPlaying

            if (lastDispatchedRef.current === key && !isPauseEvent) {
                if (usePlaybackStore.getState().isRenderingFrame) {
                    usePlaybackStore.getState().setRenderingFrame(false)
                }
                return
            }
            lastDispatchedRef.current = key

            if (isPlaying) {
                // Concurrent React scheduling during high-speed playback:
                // Keeps the slider thumb and timer clock 60 FPS responsive while table heavy rerenders are interruptible
                startTransition(() => {
                    useSettingsStore.getState().setSimulationTime(newEnd)
                    onChange(newStart, newEnd)
                })
            } else if (isPauseEvent) {
                // Non-blocking pause flush: Ensures the paused timestamp is dispatched cleanly
                // without freezing the main thread or delaying the Pause button state flip!
                startTransition(() => {
                    useSettingsStore.getState().setSimulationTime(newEnd)
                    onChange(newStart, newEnd)
                })
            } else {
                // Sync dispatch on manual scrubbing/click
                useSettingsStore.getState().setSimulationTime(newEnd)
                onChange(newStart, newEnd)
            }

            // Post-Paint Gating Acknowledgment:
            // Wait for the browser to complete layout and paint before releasing the next playback frame
            requestAnimationFrame(() => {
                if (usePlaybackStore.getState().isRenderingFrame) {
                    usePlaybackStore.getState().setRenderingFrame(false)
                }
            })
        }, [snapshotMin, startMin, endMin, viewMode, isLive, isPlaying, onChange])

        // Global Keyboard Shortcuts for Playback & Scrubbing
        useEffect(() => {
            const handleKeyDown = (e: KeyboardEvent) => {
                if (
                    e.target instanceof HTMLInputElement ||
                    e.target instanceof HTMLTextAreaElement ||
                    e.target instanceof HTMLSelectElement
                ) {
                    return
                }

                if (e.code === 'Space') {
                    e.preventDefault()
                    React.startTransition(() => {
                        togglePlayback()
                    })
                } else if (e.code === 'ArrowLeft') {
                    e.preventDefault()
                    React.startTransition(() => {
                        if (e.shiftKey) jumpTime(-5)
                        else jumpTime(-1)
                    })
                } else if (e.code === 'ArrowRight') {
                    e.preventDefault()
                    React.startTransition(() => {
                        if (e.shiftKey) jumpTime(5)
                        else jumpTime(1)
                    })
                } else if (e.code === 'Home') {
                    e.preventDefault()
                    if (availableMinutes.length > 0) setSnapshotMin(availableMinutes[0]!)
                    else setSnapshotMin(minMinutes)
                } else if (e.code === 'End') {
                    e.preventDefault()
                    if (availableMinutes.length > 0) setSnapshotMin(availableMinutes[availableMinutes.length - 1]!)
                    else setSnapshotMin(maxMinutes)
                }
            }

            window.addEventListener('keydown', handleKeyDown)
            return () => window.removeEventListener('keydown', handleKeyDown)
        }, [togglePlayback, jumpTime, availableMinutes, minMinutes, maxMinutes, setSnapshotMin])

        const [hoverPct, setHoverPct] = useState<number | null>(null)
        const [hoverMin, setHoverMin] = useState<number | null>(null)

        const handleTrackMouseMove = useCallback(
            (e: React.MouseEvent<HTMLDivElement>) => {
                const rect = e.currentTarget.getBoundingClientRect()
                if (!rect.width) return
                const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
                const pct = (x / rect.width) * 100
                const rawMin = Math.round(minMinutes + (pct / 100) * (maxMinutes - minMinutes))
                const snapped = snapToNearest(rawMin)
                setHoverPct(pct)
                setHoverMin(snapped)
            },
            [minMinutes, maxMinutes, snapToNearest],
        )

        const handleTrackMouseLeave = useCallback(() => {
            setHoverPct(null)
            setHoverMin(null)
        }, [])

        const handlePreset = useCallback(
            (label: string, minsBack: number | null) => {
                setActivePreset(label)
                setIsPlaying(false)
                if (viewMode === 'comparison') {
                    if (minsBack === null) {
                        setStartMin(minMinutes)
                        setEndMin(maxMinutes)
                    } else {
                        setEndMin(maxMinutes)
                        setStartMin(Math.max(minMinutes, maxMinutes - minsBack))
                    }
                } else {
                    // In snapshot mode, presets jump to the time
                    if (minsBack === null) {
                        setSnapshotMin(maxMinutes)
                    } else {
                        setSnapshotMin(Math.max(minMinutes, maxMinutes - minsBack))
                    }
                }
            },
            [viewMode, minMinutes, maxMinutes, setIsPlaying, setSnapshotMin],
        )

        const startPct = ((startMin - minMinutes) / (maxMinutes - minMinutes)) * 100
        const endPct = ((endMin - minMinutes) / (maxMinutes - minMinutes)) * 100
        const snapPct = ((snapshotMin - minMinutes) / (maxMinutes - minMinutes)) * 100

        // PERF FIX: Downsample time dots to max 60 to prevent layout thrashing
        const downsampledDots = useMemo(() => {
            if (availableMinutes.length <= 60) return availableMinutes
            const step = Math.ceil(availableMinutes.length / 60)
            return availableMinutes.filter((_, i) => i % step === 0)
        }, [availableMinutes])

        return (
            <div
                className={cn(
                    'w-full flex flex-col gap-1 sm:gap-1.5 xl:gap-2 p-1.5 sm:p-2 xl:p-2.5 bg-white dark:bg-slate-900 backdrop-blur-3xl rounded-xl sm:rounded-2xl border transition-all duration-300',
                    isPlaying
                        ? 'border-indigo-500/60 dark:border-indigo-400/50 shadow-[0_8px_32px_rgba(99,102,241,0.2)] ring-2 ring-indigo-400/30'
                        : 'border-slate-200/80 dark:border-slate-800 shadow-[0_8px_32px_-4px_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-[0_8px_32px_-4px_rgba(0,0,0,0.5),0_2px_8px_rgba(0,0,0,0.4)] hover:border-slate-300 dark:hover:border-slate-700',
                    className,
                )}
            >
                {/* All Controls (Adaptive: Single Unified Row on Tablet & Desktop in Live Mode; Row 2 for Snapshot Deck on Tablet) */}
                <div className="flex flex-col items-center gap-1 sm:gap-1.5 w-full max-w-full shrink-0">
                    {/* Top Row: Left Cluster (Symbol & LTP + Mode) | Center Cluster (Live Satellite Beacon / Desktop Snapshot Deck) | Right Cluster (Presets + Time + Pin) */}
                    <div className="flex items-center justify-between gap-1 sm:gap-1.5 xl:gap-2.5 w-full max-w-full shrink-0 flex-nowrap">
                        {/* Left: Institutional Symbol & Index LTP Chip + Mode Segmented Tab */}
                        <div className="flex items-center gap-1 sm:gap-1.5 xl:gap-2 shrink-0">
                            {(displayLtp > 0 || effectiveSymbol) && (
                                <div className="flex items-center gap-1 sm:gap-1.5 xl:gap-2 bg-white/90 dark:bg-slate-800/90 px-1.5 sm:px-2 xl:px-3 py-0.5 sm:py-0.5 xl:py-1 rounded-full border border-slate-200/90 dark:border-slate-700/70 shrink-0 shadow-xs hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                                    {effectiveSymbol && (
                                        <span className="px-1.5 sm:px-2 py-0.5 rounded-md bg-linear-to-r from-indigo-500/15 via-purple-500/10 to-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-200/80 dark:border-indigo-800/60 text-[8px] sm:text-[9px] xl:text-[11px] font-black uppercase tracking-wider whitespace-nowrap shadow-2xs">
                                            {effectiveSymbol}
                                        </span>
                                    )}
                                    {displayLtp > 0 ? (
                                        <>
                                            <span className="text-[10px] sm:text-[11px] xl:text-[13.5px] font-mono font-black tabular-nums text-slate-800 dark:text-slate-100 whitespace-nowrap tracking-tight">
                                                {displayLtp.toLocaleString('en-IN', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </span>
                                            <div
                                                className={`flex items-center gap-0.5 xl:gap-1 px-1 sm:px-1.5 xl:px-2 py-0.5 rounded-full text-[8px] sm:text-[8.5px] xl:text-[10.5px] font-black tabular-nums whitespace-nowrap shadow-2xs border ${
                                                    displayDayChange >= 0
                                                        ? 'bg-emerald-50 text-emerald-600 border-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50'
                                                        : 'bg-rose-50 text-rose-600 border-rose-200/80 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800/50'
                                                }`}
                                            >
                                                {displayDayChange >= 0 ? (
                                                    <TrendingUp className="w-2 sm:w-2.5 xl:w-3.5 h-2 sm:h-2.5 xl:h-3.5 shrink-0" />
                                                ) : (
                                                    <TrendingDown className="w-2 sm:w-2.5 xl:w-3.5 h-2 sm:h-2.5 xl:h-3.5 shrink-0" />
                                                )}
                                                <span>
                                                    {displayDayChange >= 0 ? '+' : ''}
                                                    {displayDayChange.toFixed(2)}
                                                </span>
                                                <span className="opacity-80">
                                                    ({displayDayChangeP >= 0 ? '+' : ''}
                                                    {displayDayChangeP.toFixed(2)}%)
                                                </span>
                                            </div>
                                        </>
                                    ) : (
                                        <span className="text-[8.5px] sm:text-[9.5px] font-bold text-slate-500 dark:text-slate-400">
                                            Historical
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Mode Segmented Tab */}
                            {!hideModeToggle && (
                                <div className="flex bg-slate-200/70 dark:bg-slate-800/80 p-0.5 xl:p-1 rounded-full border border-slate-300/70 dark:border-slate-700/60 shrink-0 shadow-inner">
                                    <button
                                        onClick={() => {
                                            onViewModeChange('snapshot')
                                            setIsPlaying(false)
                                        }}
                                        className={`px-1.5 sm:px-2 xl:px-3 py-0.5 sm:py-0.5 xl:py-1 flex items-center gap-1 sm:gap-1.5 rounded-full text-[8px] sm:text-[9px] xl:text-[11px] font-black transition-all duration-200 ${
                                            viewMode === 'snapshot'
                                                ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-sm shadow-indigo-500/30'
                                                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100'
                                        }`}
                                    >
                                        <Clock className="w-2.5 h-2.5 xl:w-3 xl:h-3 shrink-0" />
                                        <span>History</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            onViewModeChange('comparison')
                                            setIsPlaying(false)
                                        }}
                                        className={`px-1.5 sm:px-2 xl:px-3 py-0.5 sm:py-0.5 xl:py-1 flex items-center gap-1 sm:gap-1.5 rounded-full text-[8px] sm:text-[9px] xl:text-[11px] font-black transition-all duration-200 ${
                                            viewMode === 'comparison'
                                                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-sm shadow-emerald-500/30'
                                                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100'
                                        }`}
                                    >
                                        <Zap className="w-2.5 h-2.5 xl:w-3 xl:h-3 shrink-0" />
                                        <span>Live</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Center Cluster:
                        - Live Mode: Satellite Beacon on Top Row across ALL screens (Tablet & Desktop)
                        - Snapshot Mode: Master Playback Deck on Desktop (xl+), tablet renders below on row 2
                    */}
                        {viewMode === 'comparison' ? (
                            <div
                                className="flex items-center justify-center gap-1 sm:gap-1.5 xl:gap-2 px-2 sm:px-2.5 xl:px-3.5 py-0.5 sm:py-0.5 xl:py-1 bg-emerald-50/90 dark:bg-emerald-950/40 rounded-full border border-emerald-300/70 dark:border-emerald-700/60 text-[8px] sm:text-[9px] xl:text-[11px] font-black text-emerald-700 dark:text-emerald-300 shadow-xs shrink-0 select-none animate-in fade-in duration-200 whitespace-nowrap"
                                title={`Live Market Feed: Source ${tierInfo.label} (${tierInfo.detail}) | Last updated at ${liveUpdateText} ${liveLatency > 0 ? `(${liveLatency}ms latency)` : ''}`}
                            >
                                <span className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2 xl:h-2.5 xl:w-2.5 shrink-0">
                                    <span
                                        className={cn(
                                            'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
                                            tierInfo.pingClass,
                                        )}
                                    ></span>
                                    <span
                                        className={cn(
                                            'relative inline-flex rounded-full h-1.5 w-1.5 sm:h-2 sm:w-2 xl:h-2.5 xl:w-2.5',
                                            tierInfo.dotClass,
                                        )}
                                    ></span>
                                </span>
                                <span className="uppercase tracking-wider text-[7.5px] sm:text-[8.5px] xl:text-[10px] font-extrabold text-emerald-600 dark:text-emerald-400">
                                    LIVE FEED
                                </span>

                                {/* Source Tier Badge */}
                                <span
                                    className={cn(
                                        'px-1 sm:px-1.5 py-0.2 sm:py-0.5 rounded-full border text-[7px] sm:text-[7.5px] xl:text-[9px] font-black uppercase tracking-tight shadow-2xs cursor-default',
                                        tierInfo.colorClass,
                                    )}
                                    title={`Feed Origin: ${tierInfo.label} (${tierInfo.detail})`}
                                >
                                    {tierInfo.label}
                                </span>

                                <div className="w-px h-2.5 sm:h-3 xl:h-3.5 bg-emerald-200 dark:bg-emerald-800/80" />
                                <span className="font-mono tabular-nums text-[9px] sm:text-[10px] xl:text-[12px] font-black text-slate-800 dark:text-slate-100">
                                    {liveUpdateText}
                                </span>
                                {liveLatency > 0 && (
                                    <span className="text-[7.5px] sm:text-[8.5px] opacity-75 font-mono text-emerald-600 dark:text-emerald-400 hidden sm:inline">
                                        ({liveLatency}ms)
                                    </span>
                                )}
                            </div>
                        ) : (
                            /* Desktop Snapshot Playback Deck */
                            <div className="hidden xl:flex items-center justify-center gap-0.5 xl:gap-1 bg-slate-200/60 dark:bg-slate-800/80 p-0.5 xl:p-1 rounded-full border border-slate-300/70 dark:border-slate-700/60 shadow-inner shrink-0 flex-nowrap">
                                {renderPlaybackDeck()}
                            </div>
                        )}

                        {/* Right Cluster: Presets Dropdown & Time Selector & Pin Button */}
                        <div className="flex items-center gap-1 sm:gap-1.5 xl:gap-2.5 shrink-0">
                            {/* Presets Dropdown */}
                            <div className="relative flex items-center shrink-0">
                                <div className="relative flex items-center bg-white/90 dark:bg-slate-800/90 rounded-full border border-slate-200/90 dark:border-slate-700/70 shadow-xs hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                                    <select
                                        value={activePreset || 'Day'}
                                        onChange={(e) => {
                                            const val = e.target.value
                                            if (!val) return
                                            const preset = PRESETS.find((p) => p.label === val)
                                            if (preset) handlePreset(preset.label, preset.min)
                                        }}
                                        className="appearance-none bg-transparent text-slate-800 dark:text-slate-100 text-[8.5px] sm:text-[9.5px] xl:text-[11px] font-black pl-2 sm:pl-2.5 xl:pl-3.5 pr-4 sm:pr-5 xl:pr-6 py-0.5 sm:py-0.5 xl:py-1 outline-none cursor-pointer z-10"
                                        title="Time Range Preset"
                                    >
                                        {PRESETS.map((preset) => (
                                            <option
                                                key={preset.label}
                                                value={preset.label}
                                                className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800 font-bold"
                                            >
                                                {preset.label}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className="w-2.5 h-2.5 sm:w-3 sm:h-3 absolute right-1 sm:right-1.5 xl:right-2 pointer-events-none text-slate-400" />
                                </div>
                            </div>

                            {/* Interactive Time Picker Popover */}
                            {renderInteractiveTimePicker(
                                'px-2 sm:px-2.5 xl:px-3 py-0.5 sm:py-0.5 xl:py-1 text-[8.5px] sm:text-[9.5px] xl:text-[11px]',
                            )}

                            {/* Pin to Top Button */}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setIsSlicerSticky(!isSlicerSticky)
                                }}
                                className={`flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 xl:px-2.5 py-0.5 sm:py-0.5 xl:py-1 rounded-full text-[8px] sm:text-[9px] xl:text-[10.5px] font-black transition-all duration-200 border shadow-xs shrink-0 select-none ${
                                    isSlicerSticky
                                        ? 'bg-indigo-600 dark:bg-indigo-600 text-white border-indigo-500 shadow-indigo-500/30 ring-1 ring-indigo-400/40'
                                        : 'bg-white/90 dark:bg-slate-800/90 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 border-slate-200/90 dark:border-slate-700/70 hover:border-slate-300 dark:hover:border-slate-600'
                                }`}
                                title={
                                    isSlicerSticky
                                        ? 'Unpin Slicer from Top'
                                        : 'Pin Slicer to Top of Screen while Scrolling'
                                }
                            >
                                {isSlicerSticky ? (
                                    <Pin className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-white shrink-0" />
                                ) : (
                                    <PinOff className="w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0" />
                                )}
                                <span>{isSlicerSticky ? 'Pinned' : 'Pin'}</span>
                            </button>

                            {/* Discipline Shield Visibility Toggle */}
                            {!isHistoricalMode && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        toggleDisciplineShield()
                                    }}
                                    className={`flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 xl:px-2.5 py-0.5 sm:py-0.5 xl:py-1 rounded-full text-[8px] sm:text-[9px] xl:text-[10.5px] font-black transition-all duration-200 border shadow-xs shrink-0 select-none cursor-pointer ${
                                        isDisciplineShieldVisible
                                            ? 'bg-emerald-500/15 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-400/60 dark:border-emerald-600/50 hover:bg-emerald-500/25 ring-1 ring-emerald-400/30'
                                            : 'bg-white/90 dark:bg-slate-800/90 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 border-slate-200/90 dark:border-slate-700/70 hover:border-slate-300 dark:hover:border-slate-600'
                                    }`}
                                    title={
                                        isDisciplineShieldVisible
                                            ? 'Click to Hide Trader Discipline Shield & Rules Bar'
                                            : 'Click to Show Trader Discipline Shield & Rules Bar'
                                    }
                                >
                                    <ShieldAlert
                                        className={`w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0 ${
                                            isDisciplineShieldVisible
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : 'text-slate-400'
                                        }`}
                                    />
                                    <span className="hidden sm:inline">
                                        {isDisciplineShieldVisible ? 'Shield Active' : 'Shield Off'}
                                    </span>
                                    <span className="sm:hidden">
                                        {isDisciplineShieldVisible ? 'Shield' : 'Off'}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Tablet-Only Row 2 for Master Playback Deck when in Snapshot Mode */}
                    {viewMode === 'snapshot' && (
                        <div className="flex xl:hidden items-center justify-center w-full mt-0.5 shrink-0 animate-in fade-in duration-150">
                            <div className="flex items-center justify-center gap-0.5 sm:gap-1 bg-slate-200/60 dark:bg-slate-800/80 p-0.5 rounded-full border border-slate-300/70 dark:border-slate-700/60 shadow-inner shrink-0 flex-nowrap w-full sm:w-auto">
                                {renderPlaybackDeck()}
                            </div>
                        </div>
                    )}
                </div>

                {/* Bottom Row: Ultra-Premium Slider Track */}
                <div className="flex items-center gap-1.5 sm:gap-2 xl:gap-3 px-1 sm:px-1.5 flex-1 min-w-[200px] w-full text-[9px] sm:text-[10px] xl:text-[11px] font-bold text-slate-400 tracking-wide uppercase">
                    <span className="px-2 sm:px-2.5 py-0.5 rounded-md sm:rounded-lg bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700/60 text-[8.5px] sm:text-[9.5px] xl:text-[10px] font-mono font-bold text-slate-600 dark:text-slate-300 tabular-nums whitespace-nowrap shadow-2xs shrink-0">
                        {formatMinutes(minMinutes)}
                    </span>

                    <div
                        className="flex-1 relative h-5 sm:h-5.5 xl:h-6 flex items-center group/slider"
                        onMouseMove={handleTrackMouseMove}
                        onMouseLeave={handleTrackMouseLeave}
                    >
                        {/* Hover Time Tooltip Badge (positioned cleanly below track) */}
                        {hoverPct !== null && hoverMin !== null && !isPlaying && (
                            <div
                                className="absolute top-6.5 px-2.5 py-1 rounded-lg bg-slate-950/95 dark:bg-slate-900/95 text-indigo-300 text-[10px] font-mono font-bold pointer-events-none shadow-xl transform -translate-x-1/2 z-30 transition-all duration-75 border border-indigo-500/40 backdrop-blur-md flex items-center gap-1.5"
                                style={{ left: `${Math.max(6, Math.min(94, hoverPct))}%` }}
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse shrink-0" />
                                <span>{formatMinutes(hoverMin)}</span>
                            </div>
                        )}

                        {/* Track Background */}
                        <div className="absolute left-0 right-0 h-[6px] bg-slate-200/80 dark:bg-slate-800 rounded-full overflow-hidden shadow-[inset_0_1px_3px_rgba(0,0,0,0.15)] border border-slate-200/50 dark:border-slate-700/40" />

                        {viewMode === 'comparison' ? (
                            <>
                                {/* Active Comparison Track */}
                                <div
                                    className="absolute h-[6px] bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full shadow-[0_0_12px_rgba(16,185,129,0.4)]"
                                    style={{ left: startPct + '%', right: 100 - endPct + '%' }}
                                />

                                <input
                                    type="range"
                                    min={minMinutes}
                                    max={maxMinutes}
                                    value={startMin}
                                    onChange={(e) => {
                                        const v = parseInt(e.target.value)
                                        if (v <= endMin) {
                                            setStartMin(v)
                                            setActivePreset(null)
                                        }
                                    }}
                                    className="absolute w-full appearance-none pointer-events-none bg-transparent h-6"
                                    style={{ zIndex: 3 }}
                                />
                                <input
                                    type="range"
                                    min={minMinutes}
                                    max={maxMinutes}
                                    value={endMin}
                                    onChange={(e) => {
                                        const v = parseInt(e.target.value)
                                        if (v >= startMin) {
                                            setEndMin(v)
                                            setActivePreset(null)
                                        }
                                    }}
                                    className="absolute w-full appearance-none pointer-events-none bg-transparent h-6"
                                    style={{ zIndex: 4 }}
                                />
                            </>
                        ) : (
                            <>
                                {/* Snapshot Track Fill with Aurora Cyan-Indigo-Purple Gradient */}
                                <div
                                    className={`absolute h-[6px] bg-gradient-to-r from-teal-400 via-indigo-500 to-purple-500 rounded-full shadow-[0_0_16px_rgba(99,102,241,0.5)] ${
                                        isPlaying ? 'transition-none' : 'transition-all duration-150'
                                    }`}
                                    style={{ left: 0, right: Math.max(0, 100 - snapPct) + '%' }}
                                />

                                {/* Snapshot marker (Thumb overlay) */}
                                <div
                                    className={`absolute h-4.5 w-4.5 bg-white border-[3px] border-indigo-500 rounded-full shadow-[0_0_12px_rgba(99,102,241,0.7)] pointer-events-none z-10 ${
                                        isPlaying
                                            ? 'transition-none ring-4 ring-indigo-400/40'
                                            : 'transition-all duration-150'
                                    }`}
                                    style={{
                                        left: Math.min(100, Math.max(0, snapPct)) + '%',
                                        transform: 'translateX(-50%)',
                                    }}
                                />

                                {/* Available time dots — PERF FIX: Downsampled to max 60 */}
                                {downsampledDots.map((m) => {
                                    const pct = ((m - minMinutes) / (maxMinutes - minMinutes)) * 100
                                    return (
                                        <div
                                            key={m}
                                            className="absolute w-1 h-1 rounded-full bg-white/70 dark:bg-slate-300 mix-blend-overlay pointer-events-none"
                                            style={{ left: pct + '%', top: '50%', transform: 'translate(-50%, -50%)' }}
                                        />
                                    )
                                })}

                                <input
                                    type="range"
                                    min={minMinutes}
                                    max={maxMinutes}
                                    value={snapshotMin}
                                    onChange={(e) => {
                                        setSnapshotMin(snapToNearest(parseInt(e.target.value)))
                                        setActivePreset(null)
                                        setIsPlaying(false)
                                    }}
                                    className="absolute w-full appearance-none pointer-events-auto bg-transparent h-6"
                                    style={{ zIndex: 3 }}
                                />
                            </>
                        )}

                        {/* PERF FIX: Use stable style HTML reference instead of recreating on every render */}
                        <style dangerouslySetInnerHTML={SLIDER_STYLE_HTML} />
                    </div>

                    <span className="px-2 sm:px-2.5 py-0.5 rounded-md sm:rounded-lg bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700/60 text-[8.5px] sm:text-[9.5px] xl:text-[10px] font-mono font-bold text-slate-600 dark:text-slate-300 tabular-nums whitespace-nowrap shadow-2xs shrink-0">
                        {formatMinutes(maxMinutes)}
                    </span>
                </div>

                {/* ─── ULTRA-PREMIUM TRADER DISCIPLINE & PSYCHOLOGY BAR ─────────────────── */}
                {/* Hidden when History mode is clicked / active OR when user toggles Shield off */}
                {!isHistoricalMode && isDisciplineShieldVisible && (
                    <div className="w-full flex flex-col pt-1 border-t border-slate-200/80 dark:border-slate-800/80 select-none animate-in fade-in duration-200">
                        {/* Luminous Cockpit: Strictly 2 Clean Rows in all views (Tablet & Desktop) */}
                        <div className="w-full rounded-xl bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200/90 dark:border-slate-800 shadow-xs overflow-hidden flex flex-col">
                        {/* ROW 1: The Trader's Creed & Active Telemetry (Strictly 1 Single Row) */}
                        <div className="flex items-center justify-between gap-1 sm:gap-1.5 xl:gap-2 px-2 sm:px-2.5 xl:px-3 py-0.5 sm:py-0.5 xl:py-1 border-b border-slate-200/70 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-850 shrink-0 w-full flex-nowrap">
                            {/* Left: Boldly Highlighted Mantra */}
                            <div className="flex items-center gap-1 sm:gap-1.5 xl:gap-2 shrink-0">
                                {/* REFINED BALANCED HERO BADGE */}
                                <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 xl:px-3 py-0.5 rounded-full bg-slate-50 dark:bg-slate-800/90 border border-slate-200/90 dark:border-slate-700/70 shadow-2xs tracking-wider shrink-0">
                                    <ShieldAlert className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                    <span className="text-[9px] sm:text-[10px] xl:text-[11px] uppercase tracking-wide whitespace-nowrap text-slate-700 dark:text-slate-200 font-extrabold">
                                        {TRADING_PSYCHOLOGY_CONFIG.MANTRA_PREFIX}{' '}
                                        <span className="text-emerald-600 dark:text-emerald-400 font-black tracking-wide">
                                            {TRADING_PSYCHOLOGY_CONFIG.MANTRA_TRADER}
                                        </span>
                                        {TRADING_PSYCHOLOGY_CONFIG.MANTRA_MIDDLE}{' '}
                                        <span className="text-rose-600 dark:text-rose-400 font-black tracking-wider">
                                            {TRADING_PSYCHOLOGY_CONFIG.MANTRA_GAMBLER}
                                        </span>
                                    </span>
                                </div>
                                <span className="hidden sm:inline text-slate-400 dark:text-slate-500 font-bold">•</span>
                                <span className="hidden sm:inline text-[8px] sm:text-[8.5px] xl:text-[10px] font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                    {TRADING_PSYCHOLOGY_CONFIG.MANTRA_SUBTITLE}
                                </span>
                            </div>

                            {/* Center: Institutional Trading Psychology Quotations (Compact & Responsive for Tablet/Desktop) */}
                            <div className="hidden md:flex items-center gap-1.5 xl:gap-2 flex-1 justify-center px-1 sm:px-2 min-w-0 overflow-hidden select-none">
                                {/* Desktop View (>= 1280px / xl): Both Quotations displayed side-by-side in high-contrast capsules */}
                                <div className="hidden xl:flex items-center gap-1.5 2xl:gap-2 min-w-0">
                                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700/60 shadow-2xs">
                                        <span className="text-[9.5px] leading-none">🧠</span>
                                        <span className="text-[8.5px] xl:text-[9.5px] font-bold text-slate-700 dark:text-slate-200 italic whitespace-nowrap">
                                            &ldquo;{TRADING_PSYCHOLOGY_CONFIG.QUOTE_SURVIVAL}&rdquo;
                                        </span>
                                    </div>
                                    <span className="text-slate-300 dark:text-slate-600 font-bold">•</span>
                                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700/60 shadow-2xs">
                                        <span className="text-[9.5px] leading-none">💎</span>
                                        <span className="text-[8.5px] xl:text-[9.5px] font-bold text-slate-700 dark:text-slate-200 italic whitespace-nowrap">
                                            &ldquo;{TRADING_PSYCHOLOGY_CONFIG.QUOTE_CLARITY}&rdquo;
                                        </span>
                                    </div>
                                </div>

                                {/* Tablet View (md to xl): Dynamic rotating quotation with smooth animation */}
                                <div className="xl:hidden flex items-center justify-center min-w-0 overflow-hidden">
                                    <div 
                                        key={activeQuoteIdx}
                                        className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700/60 shadow-2xs animate-in fade-in duration-300 max-w-full truncate"
                                    >
                                        <span className="text-[9.5px] leading-none shrink-0">
                                            {activeQuoteIdx === 0 ? '🧠' : '💎'}
                                        </span>
                                        <span className="text-[8.5px] sm:text-[9px] font-bold text-slate-700 dark:text-slate-200 italic truncate">
                                            &ldquo;{TRADING_PSYCHOLOGY_CONFIG.QUOTATIONS[activeQuoteIdx]}&rdquo;
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Right: Active Status & Matrix Drawer Toggle */}
                            <div className="flex items-center gap-1 sm:gap-1.5 xl:gap-2 shrink-0 ml-auto flex-nowrap">
                                <div className="hidden lg:flex items-center gap-1 sm:gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-800/60 text-[8px] sm:text-[8.5px] font-extrabold tracking-wider uppercase shadow-2xs shrink-0">
                                    <span className="relative flex h-1.5 w-1.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                                    </span>
                                    <span>{TRADING_PSYCHOLOGY_CONFIG.STATUS_BADGE}</span>
                                </div>

                                <button
                                    type="button"
                                    onClick={togglePsychologyExpanded}
                                    className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 rounded-full text-[8px] sm:text-[8.5px] xl:text-[9px] font-black tracking-wide uppercase transition-all duration-200 border cursor-pointer shadow-2xs shrink-0 ${
                                        isPsychologyExpanded
                                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-emerald-500/20'
                                            : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200/90 dark:border-slate-700/80 hover:border-emerald-300 hover:text-emerald-600'
                                    }`}
                                    title={
                                        isPsychologyExpanded
                                            ? 'Collapse Mindset Rules'
                                            : `Expand Mindset Rules (${TRADING_PSYCHOLOGY_CONFIG.AVOID_ITEMS.length})`
                                    }
                                >
                                    <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-emerald-500" />
                                    <span>
                                        {isPsychologyExpanded
                                            ? 'Hide Rules'
                                            : `Rules Matrix (${TRADING_PSYCHOLOGY_CONFIG.AVOID_ITEMS.length})`}
                                    </span>
                                    <ChevronDown
                                        className={`w-2.5 h-2.5 sm:w-3 sm:h-3 transition-transform duration-200 ${isPsychologyExpanded ? 'rotate-180' : ''}`}
                                    />
                                </button>

                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        toggleDisciplineShield()
                                    }}
                                    className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full text-[8px] sm:text-[8.5px] font-bold text-slate-500 hover:text-rose-600 dark:text-slate-400 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 border border-slate-200/80 dark:border-slate-700/60 transition-all cursor-pointer shadow-2xs shrink-0"
                                    title="Hide Discipline Shield Bar (Can be re-enabled from top bar 'Shield Off' button)"
                                >
                                    <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                    <span className="hidden sm:inline">Hide</span>
                                </button>
                            </div>
                        </div>

                        {/* ROW 2: Avoid Section (Strictly 1 Row, Zero Horizontal Scroll, Auto-fit Responsive Pills) */}
                        <div className="flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 xl:px-3 py-0.5 sm:py-0.5 xl:py-1 bg-white dark:bg-slate-900 w-full overflow-hidden shrink-0 flex-nowrap">
                            {/* Danger Anchor Badge */}
                            <div className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800/60 text-[8px] sm:text-[8.5px] md:text-[9px] xl:text-[10px] font-black tracking-wider uppercase shrink-0 shadow-2xs">
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-ping shrink-0" />
                                <span>{TRADING_PSYCHOLOGY_CONFIG.AVOID_SHORT_LABEL}:</span>
                            </div>

                            {/* 9 Avoid Capsule Pills (Spans row cleanly with flex-1, zero scrollbar!) */}
                            <TooltipProvider delayDuration={100} skipDelayDuration={50}>
                                <div className="flex items-center gap-0.5 sm:gap-1 xl:gap-1.5 flex-1 min-w-0 flex-nowrap">
                                    {TRADING_PSYCHOLOGY_CONFIG.AVOID_ITEMS.map((item) => (
                                        <Tooltip key={item.id}>
                                            <TooltipTrigger asChild>
                                                <div className="flex-1 min-w-0 cursor-help group/pill">
                                                    <div className="flex items-center justify-center gap-0.5 sm:gap-1 px-1 sm:px-1.5 xl:px-2 py-0.5 sm:py-1 rounded-full text-[8px] sm:text-[8.5px] md:text-[9.5px] xl:text-[10.5px] font-bold bg-slate-50 dark:bg-slate-800/90 text-slate-700 dark:text-slate-200 border border-slate-200/90 dark:border-slate-700/80 group-hover/pill:border-rose-300 group-hover/pill:bg-rose-50/70 dark:group-hover/pill:bg-rose-950/40 group-hover/pill:text-rose-600 dark:group-hover/pill:text-rose-300 transition-all duration-150 shadow-2xs truncate">
                                                        <span className="text-[9px] sm:text-[9.5px] md:text-[10.5px] xl:text-[11.5px] leading-none shrink-0">
                                                            {item.icon}
                                                        </span>
                                                        <span className="hidden xl:inline truncate tracking-tight">
                                                            {item.label}
                                                        </span>
                                                        <span className="xl:hidden truncate tracking-tight">
                                                            {item.shortLabel || item.label}
                                                        </span>
                                                    </div>
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent
                                                side="bottom"
                                                align="center"
                                                sideOffset={8}
                                                avoidCollisions={true}
                                                className="z-[100] p-0 border-none bg-transparent shadow-none"
                                            >
                                                <div className="w-[260px] p-2.5 rounded-xl bg-white/95 dark:bg-slate-900/95 text-slate-800 dark:text-slate-100 border border-rose-300/80 dark:border-rose-500/40 shadow-xl backdrop-blur-xl flex flex-col gap-1 text-left select-none animate-in fade-in zoom-in-95 duration-100">
                                                    <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-1 gap-2">
                                                        <div className="flex items-center gap-1.5 text-[10.5px] font-black uppercase text-rose-600 dark:text-rose-400">
                                                            <span>{item.icon}</span>
                                                            <span>{item.label}</span>
                                                        </div>
                                                        <span className="text-[7.5px] font-black px-1.5 py-0.5 rounded-full bg-rose-50 dark:bg-rose-500/20 text-rose-600 dark:text-rose-300 border border-rose-200 dark:border-rose-500/30 uppercase shrink-0">
                                                            {item.badge || item.category}
                                                        </span>
                                                    </div>
                                                    <p className="text-[9.5px] text-slate-600 dark:text-slate-300 leading-snug font-sans">
                                                        {item.description}
                                                    </p>
                                                    <div className="mt-0.5 pt-1 border-t border-slate-100 dark:border-slate-800 flex items-start gap-1">
                                                        <span className="text-emerald-600 dark:text-emerald-400 text-[9px] font-black shrink-0">
                                                            RULE:
                                                        </span>
                                                        <span className="text-[9px] font-semibold text-emerald-700 dark:text-emerald-300 leading-tight">
                                                            {item.antidote}
                                                        </span>
                                                    </div>
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    ))}
                                </div>
                            </TooltipProvider>
                        </div>

                        {/* Optional Drawer: Master Tactical Matrix (Light-First Ultra-Clean Design) */}
                        {isPsychologyExpanded && (
                            <div className="p-3 bg-slate-50/90 dark:bg-slate-900/95 border-t border-emerald-500/15 dark:border-slate-800/90 flex flex-col gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                                    {TRADING_PSYCHOLOGY_CONFIG.CATEGORIES.map((cat) => {
                                        const itemsInCat = TRADING_PSYCHOLOGY_CONFIG.AVOID_ITEMS.filter(
                                            (i) => i.category === cat.key,
                                        )
                                        return (
                                            <div
                                                key={cat.key}
                                                className="flex flex-col gap-2 p-2.5 rounded-xl bg-white dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/60 shadow-2xs"
                                            >
                                                <div className="flex items-center justify-between pb-1 border-b border-slate-200/60 dark:border-slate-700/60">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-sm">{cat.icon}</span>
                                                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                                            {cat.label}
                                                        </span>
                                                    </div>
                                                    <span className="text-[8px] font-mono font-bold text-slate-500">
                                                        {itemsInCat.length} Traps
                                                    </span>
                                                </div>

                                                <div className="flex flex-col gap-2">
                                                    {itemsInCat.map((item) => (
                                                        <div
                                                            key={item.id}
                                                            className="p-2 rounded-lg bg-slate-50/70 dark:bg-slate-800/80 border border-slate-200/70 dark:border-slate-700/70 hover:border-rose-300 transition-colors flex flex-col gap-1"
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="text-xs">{item.icon}</span>
                                                                    <span className="text-[10px] font-black text-rose-600 dark:text-rose-400">
                                                                        {item.label}
                                                                    </span>
                                                                </div>
                                                                <span className="text-[7.5px] font-bold px-1.5 py-0.5 rounded bg-slate-200/70 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                                                    {item.badge}
                                                                </span>
                                                            </div>
                                                            <p className="text-[9px] text-slate-600 dark:text-slate-300 leading-snug">
                                                                {item.description}
                                                            </p>
                                                            <div className="flex items-start gap-1 pt-0.5 text-[8.5px] text-emerald-700 dark:text-emerald-400 font-medium">
                                                                <span className="font-black shrink-0">✓ DO:</span>
                                                                <span className="text-emerald-600 dark:text-emerald-300">
                                                                    {item.antidote}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>

                                {/* Affirmation Footer Strip */}
                                <div className="flex items-center justify-between px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 text-emerald-800 dark:text-emerald-300 text-[9.5px] font-black tracking-wider uppercase shadow-2xs">
                                    <div className="flex items-center gap-2">
                                        <Sparkles className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                        <span>{TRADING_PSYCHOLOGY_CONFIG.AFFIRMATION}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={togglePsychologyExpanded}
                                        className="text-[8.5px] font-extrabold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white underline cursor-pointer"
                                    >
                                        Close Matrix
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                )}
            </div>
        )
    },
)
